/**
 * AppShell — responsive layout: a left navigation rail, a scrollable main
 * column, and an optional right rail.
 *
 * Breakpoints (driven by `useMediaQuery`, so only one instance of each region
 * lives in the DOM — no duplicate data-testids between the persistent layout
 * and its drawer fallback):
 * - ≥ lg (1024px): persistent left sidebar.
 * - < lg: a top bar with a hamburger that opens the nav in a left Sheet.
 * - ≥ xl (1280px): persistent right rail (when `aside` is provided).
 * - < xl: a quiet trigger that opens the `aside` in a right Sheet.
 *
 * No router or data dependencies — apps compose this with their own nav config.
 */
import { Menu } from 'lucide-react';
import * as React from 'react';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../components/sheet.tsx';
import { cn } from '../lib/cn.ts';
import { useMediaQuery } from '../lib/use-media-query.ts';
import { type NavGroup, SidebarNav } from './sidebar-nav.tsx';
import { TopBar, type TopBarProps } from './top-bar.tsx';

export interface AppShellProps {
  /** Navigation groups passed to SidebarNav. */
  navGroups: NavGroup[];
  /** Logo / wordmark area at the top of the sidebar. */
  logo?: React.ReactNode;
  /** Optional content below the logo and above the nav (e.g. an account chip + search). */
  sidebarHeader?: React.ReactNode;
  /** Footer area at the bottom of the sidebar (user menu, etc.). */
  sidebarFooter?: React.ReactNode;
  /** TopBar props — if omitted, no desktop top bar is rendered. */
  topBar?: TopBarProps;
  /** Main content. */
  children: React.ReactNode;
  /** Optional right rail (e.g. the reception live-call panel). */
  aside?: React.ReactNode;
  /** Label for the compact trigger that opens the rail below xl. */
  asideTriggerLabel?: string;
  /** Accessible title for the rail drawer + nav drawer. */
  asideTitle?: string;
  /** Accessible label for the mobile nav hamburger. */
  mobileNavLabel?: string;
  className?: string;
  'data-testid'?: string;
}

function AppShell({
  navGroups,
  logo,
  sidebarHeader,
  sidebarFooter,
  topBar,
  children,
  aside,
  asideTriggerLabel = 'Details',
  asideTitle = 'Details',
  mobileNavLabel = 'Open navigation',
  className,
  'data-testid': testId,
}: AppShellProps) {
  const isDesktopNav = useMediaQuery('(min-width: 1024px)');
  const isWideAside = useMediaQuery('(min-width: 1280px)');
  const [navOpen, setNavOpen] = React.useState(false);
  const [asideOpen, setAsideOpen] = React.useState(false);

  const asideTrigger =
    aside && !isWideAside ? (
      <button
        type="button"
        data-testid="app-shell-aside-trigger"
        aria-label={asideTriggerLabel}
        onClick={() => setAsideOpen(true)}
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
          'border border-[var(--color-border)] bg-[var(--color-alabaster)]',
          'font-[var(--font-mono)] text-[11px] uppercase tracking-widest text-[var(--color-stone-700)]',
          'transition-colors hover:bg-[var(--color-canvas)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
        )}
      >
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[var(--color-alert)]" />
        {asideTriggerLabel}
      </button>
    ) : null;

  return (
    <div
      data-testid={testId ?? 'app-shell'}
      className={cn('flex h-screen overflow-hidden bg-[var(--color-alabaster)]', className)}
    >
      {/* ── Persistent sidebar (≥ lg) ─────────────────────────────────── */}
      {isDesktopNav && (
        <aside
          data-testid="app-shell-sidebar"
          className={cn(
            'flex w-60 shrink-0 flex-col',
            // Prototype `.rail`: warm rail surface, hairline rail line.
            'border-r border-[var(--color-rail-line)]',
            'bg-[var(--color-rail-bg)]',
          )}
        >
          {logo && (
            <div className="flex h-14 shrink-0 items-center border-b border-[var(--color-rail-line)] px-4">
              {logo}
            </div>
          )}
          {sidebarHeader && <div className="shrink-0 px-3 pt-3">{sidebarHeader}</div>}
          <div className="flex-1 overflow-y-auto">
            <SidebarNav groups={navGroups} />
          </div>
          {sidebarFooter && (
            <div className="shrink-0 border-t border-[var(--color-rail-line)] p-3">
              {sidebarFooter}
            </div>
          )}
        </aside>
      )}

      {/* ── Center column ─────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile bar (< lg): hamburger + logo + trailing */}
        {!isDesktopNav && (
          <div
            data-testid="app-shell-mobile-bar"
            className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-alabaster)] px-4"
          >
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="app-shell-nav-trigger"
                  aria-label={mobileNavLabel}
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-lg',
                    'text-[var(--color-charcoal)] transition-colors hover:bg-[var(--color-canvas)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
                  )}
                >
                  <Menu aria-hidden="true" size={18} strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="app-shell-nav-sheet" className="w-72 p-0">
                <SheetTitle className="sr-only">{mobileNavLabel}</SheetTitle>
                {logo && (
                  <div className="flex h-14 shrink-0 items-center border-b border-[var(--color-border)] px-4">
                    {logo}
                  </div>
                )}
                {/*
                 * Close the drawer when a nav item is activated. The wrapper is
                 * a passive container; keyboard users activate the real nav
                 * links/buttons inside it (which carry their own key handling),
                 * so the click handler here is a pointer-only convenience.
                 */}
                {/* biome-ignore lint/a11y/noStaticElementInteractions: interactive children own keyboard activation; this only dismisses the drawer on pointer nav */}
                <div
                  role="presentation"
                  onClick={() => setNavOpen(false)}
                  className="flex-1 overflow-y-auto"
                >
                  <SidebarNav groups={navGroups} />
                </div>
                {sidebarFooter && (
                  <div className="shrink-0 border-t border-[var(--color-border)] p-3">
                    {sidebarFooter}
                  </div>
                )}
              </SheetContent>
            </Sheet>
            {logo}
            <div className="ml-auto flex items-center gap-2">
              {asideTrigger}
              {topBar?.trailing}
            </div>
          </div>
        )}

        {/* Desktop top bar (≥ lg) — the aside trigger rides here on tablet. */}
        {isDesktopNav && topBar && (
          <TopBar
            {...topBar}
            trailing={
              asideTrigger ? (
                <>
                  {asideTrigger}
                  {topBar.trailing}
                </>
              ) : (
                topBar.trailing
              )
            }
          />
        )}

        <main
          data-testid="app-shell-main"
          className="flex-1 overflow-y-auto bg-[var(--color-alabaster)]"
        >
          {/*
           * One page container for the whole platform: centered, 1280px
           * (`max-w-7xl`), generous horizontal padding — the brand-book
           * container. Pages render their content directly here and no longer
           * hand-roll their own width/padding wrappers (spec 45).
           */}
          <div data-testid="app-shell-container" className="mx-auto w-full max-w-7xl px-8 py-8">
            {children}
          </div>
        </main>
      </div>

      {/* ── Persistent right rail (≥ xl) ──────────────────────────────── */}
      {aside && isWideAside && (
        <aside
          data-testid="app-shell-aside"
          className={cn(
            'w-[336px] shrink-0 overflow-y-auto',
            'border-l border-[var(--color-border)]',
            'bg-[var(--color-canvas)] p-5',
          )}
        >
          {aside}
        </aside>
      )}

      {/* Right rail drawer (< xl) */}
      {aside && !isWideAside && (
        <Sheet open={asideOpen} onOpenChange={setAsideOpen}>
          <SheetContent
            side="right"
            data-testid="app-shell-aside-sheet"
            className="w-[360px] overflow-y-auto bg-[var(--color-canvas)] p-5 sm:max-w-[360px]"
          >
            <SheetTitle className="sr-only">{asideTitle}</SheetTitle>
            {aside}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

export { AppShell };
